// Generic interactive explorer. Renders sliders from the agent's `parameter_spec`
// and, on every (debounced) change, asks the backend to re-run the solved task's
// parameterized script with the new values and returns fresh plots. Works for ANY
// computational task — the agent decided the knobs, not us.
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { exploreProject, type ParamSpecItem, type ProjectPlot } from "@/api/projects";
import { Spinner, Alert } from "@/components/ui";

export function ParamExplorer({
  projectId,
  spec,
}: {
  projectId: string;
  spec: ParamSpecItem[];
}) {
  const { t } = useTranslation();
  const initial: Record<string, number> = {};
  for (const s of spec) initial[s.name] = s.default;

  const [params, setParams] = useState<Record<string, number>>(initial);
  const [plots, setPlots] = useState<ProjectPlot[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const seq = useRef(0);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const runExplore = (next: Record<string, number>) => {
    const myseq = ++seq.current;
    setLoading(true);
    setError(null);
    exploreProject(projectId, next)
      .then((res) => {
        if (myseq !== seq.current) return; // a newer request superseded this one
        if (res.ok) {
          setPlots(res.plot_image_urls ?? []);
        } else {
          setError(t("The model couldn't run with those values."));
        }
      })
      .catch((e) => {
        if (myseq !== seq.current) return;
        setError(e instanceof Error ? e.message : t("Explore failed."));
      })
      .finally(() => {
        if (myseq === seq.current) setLoading(false);
      });
  };

  // Initial run with defaults.
  useEffect(() => {
    runExplore(initial);
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const onChange = (name: string, value: number) => {
    const next = { ...params, [name]: value };
    setParams(next);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => runExplore(next), 280);
  };

  return (
    <div className="grid gap-6 lg:grid-cols-[280px_1fr]">
      <div className="card space-y-4">
        <h3 className="text-sm font-semibold text-ink">{t("Parameters")}</h3>
        {spec.map((s) => (
          <label key={s.name} className="block">
            <span className="flex items-center justify-between text-xs text-ink-soft">
              <span>
                {s.label}
                {s.unit ? <span className="text-ink-faint"> ({s.unit})</span> : null}
              </span>
              <span className="font-mono text-ink">{(params[s.name] ?? s.default).toFixed(s.step < 0.01 ? 3 : 2)}</span>
            </span>
            <input
              type="range"
              min={s.min}
              max={s.max}
              step={s.step}
              value={params[s.name] ?? s.default}
              onChange={(e) => onChange(s.name, Number(e.target.value))}
              className="mt-1 w-full accent-brand-600"
            />
          </label>
        ))}
        <button
          type="button"
          onClick={() => {
            setParams(initial);
            runExplore(initial);
          }}
          className="text-xs font-medium text-brand-700 hover:underline"
        >
          {t("Reset")}
        </button>
      </div>

      <div className="space-y-3">
        {error && <Alert>{error}</Alert>}
        {loading && (
          <div className="flex items-center gap-2 text-sm text-ink-soft">
            <Spinner className="h-4 w-4" /> {t("Recomputing…")}
          </div>
        )}
        {plots.length === 0 && !loading && !error && (
          <p className="text-sm text-ink-faint">{t("Move a slider to recompute the plots.")}</p>
        )}
        <div className="grid gap-3 sm:grid-cols-2">
          {plots.map((p, i) =>
            p.url ? (
              <figure key={i} className="m-0">
                <img src={p.url} alt={p.caption ?? ""} className="w-full rounded-xl border border-black/10 object-contain" />
                {p.caption && <figcaption className="mt-1 text-xs text-ink-faint">{p.caption}</figcaption>}
              </figure>
            ) : null,
          )}
        </div>
      </div>
    </div>
  );
}
